/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-02-09 14:52:43
 */
/* eslint-disable */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { produce } from 'immer'
import dayjs from 'dayjs'
import { v4 as uuid } from 'uuid'
import ProgressDialog from './ProgressDialog'
import StatusDialog from './Status'
import sensors from '../../../../utils/sensors'
import {
  useAppDispatch,
  useAppSelector,
  useSensorsRole
} from '../../../../app/hooks'
import { uploadPhotosToFolders, createFolder } from '../../../../actions/folder'
import {
  uploadToOSS,
  uploadToTencentCOS,
  fetchConfig
} from '../../../../actions/main'
import { capturePromiseException } from '../../../../utils/sentryUtils'
import { uploadPhoto } from '../../../../actions/upload'
import { notTodayCameraShotUserComment, readExif } from '../lib'
import { serverSideDecode } from '../../../../actions/watermark'
import { Modal } from 'antd'
import CONFIG from '../../../../constants/config'
import { Status as ExceptionStatus } from '../../../../utils/exceptionHelper'

const bucketName = 'net-cloud'
const folderMaxFileNum = __RELEASE_STAGE__ === 'production' ? 30000 : 22050
interface UploadDialogProps {
  fileList: File[]
  visible: boolean
  onClose: () => void
  debugEvent: (data: { actionType: string; data?: string }) => void
}

interface ExifInfo {
  userComment?: string
  width?: number
  height?: number
  [key: string]: any
}

interface UserComment {
  data?: {
    fileName?: string
    baseInfo?: {
      location?: string
      latitude?: string | number
      longitude?: string | number
      time?: string
    }
  }
  data2?: {
    fileName?: string
    userID?: string
  }
}

interface UploadPhotoParams {
  user_id: string | undefined
  group_ids: string[]
  file_name: string
  time: string
  mediaType: number
  imageInfo: {
    fileSize: number
    width?: number
    height?: number
  }
  userComment?: UserComment
  location?: string
  lat?: number
  lng?: number
  sourceType?: number
}

const UploadDialog = ({ fileList, visible, onClose, debugEvent }: UploadDialogProps, ref: React.Ref<any>) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const inviteID = useAppSelector((state) => state.group.invite_id)
  const userID = useAppSelector((state) =>
    state.main.user?.user_id
  )
  const ossToken = useAppSelector((state) => state.main.ossToken)
  const [percent, setPercent] = useState(0)
  const stopSignal = useRef(false)
  const [currentIndex, setCurrentIndex] = useState(-1)
  const [folderID, setFolderID] = useState(0)
  const [failed, setFailed] = useState(false)
  const ossServiceConfig = useRef(null)
  const role = useSensorsRole()

  const restFiles = useMemo(() => {
    const restFileList = fileList.slice(currentIndex)
    const photosNum = restFileList.filter(
      (file) => file.type.indexOf('image') > -1
    ).length
    return {
      photosNum,
      videosNum: restFileList.length - photosNum
    }
  }, [fileList, currentIndex])

  const done = useMemo(() => {
    return percent === 100 || failed
  }, [percent, failed])

  const startUpload = async (_fileList) => {
    stopSignal.current = false
    debugEvent({ actionType: 'startUpload' })
    let folderID = 0,
      photoCount = 0
    try {
      const folderInfo = await getFolderID(groupID)
      folderID = folderInfo.folderID
      photoCount = folderInfo.photoCount
      setFolderID(folderID)
    } catch (error) {
      debugEvent({
        actionType: 'getFolderIDError',
        data: JSON.stringify({ error })
      })
      onClose()
      return
    }
    if (folderID) {
      try {
        for (let index = 0; index < _fileList.length; index++) {
          if (photoCount + index + 1 > folderMaxFileNum) {
            handleOverFolderLimit()
            break
          }
          setCurrentIndex(index)

          const file = _fileList[index]
          const mediaType = file.type.indexOf('image') > -1 ? 0 : 1
          if (stopSignal.current) {
            break
          }
          const filename = await uploadToOSSAction(file)
          const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg'
          const exifInfo: ExifInfo = isJPG ? await readExif(file) : {}
          const userComment = await getUserCommentDecode(exifInfo?.userComment)
          const photoID = await uploadPhotoAction({
            filename,
            fileSize: file.size,
            mediaType,
            exifInfo,
            userComment: userComment
              ? JSON.parse(userComment)
              : notTodayCameraShotUserComment //非今拍照片or视频，无法读取exif信息
          })
          if (photoID) {
            const success = await addPhotoToFolderAction(photoID, folderID)
            if (!success) {
              handleOverFolderLimit()
              break
            }
          }
          setPercent(Math.round(((index + 1) / _fileList.length) * 100))
        }
      } catch (error) {
        console.log(error)
        debugEvent({
          actionType: 'uploadError',
          data: JSON.stringify({ error: (error as Error).message })
        })
        setFailed(true)
      }
    }
  }

  useImperativeHandle(ref, () => ({
    startUpload
  }))

  // 获取上传OSS的配置
  const getOssServiceConfig = async () => {
    // 如果已有缓存配置，直接返回
    if (ossServiceConfig.current) {
      return ossServiceConfig.current
    }
    try {
      const res = await dispatch(
        fetchConfig({
          names: CONFIG.KVParams.names
        })
      )
      if (res?.items) {
        const configItem = res.items.find(
          (item) => item.name === 'oss_service_config'
        )
        if (configItem?.value) {
          try {
            const parsedConfig = JSON.parse(configItem.value)
            ossServiceConfig.current = parsedConfig
            return parsedConfig
          } catch (parseError) {
            console.error('OSS配置解析失败:', parseError)
          }
        }
      }
      // 如果没有找到配置或解析失败，返回默认配置
      return { photo: { default_oss_service: 'ali' } }
    } catch (error) {
      console.error('获取OSS配置失败:', error)
      return { photo: { default_oss_service: 'ali' } }
    }
  }

  const uploadToOSSAction = async (file: File) => {
    let filename = uuid() + '.' + file.name.split('.').pop()
    const ossServiceConfig = await getOssServiceConfig()
    const ossPhotoConfig = ossServiceConfig?.photo
    // 照片双云上传逻辑参考 https://alidocs.dingtalk.com/i/nodes/gvNG4YZ7JnexQ7KQC1qYqm2zV2LD0oRE
    const way = getOssUploadWay(ossPhotoConfig)
    if (way === 'ali') {
      const tokenBucket = ossToken?.[bucketName]
      return uploadToOSS({
        key: `group/photo/${filename}`,
        OSSAccessKeyId: tokenBucket?.['accessid'] || '',
        policy: tokenBucket?.['policy'] || '',
        Signature: tokenBucket?.['signature'] || '',
        file: file
      })
        .then(() => {
          return filename
        })
        .catch((error) => {
          debugEvent({
            actionType: 'uploadToOSSError',
            data: JSON.stringify({ filename, error: error.message })
          })
          capturePromiseException(error)
          return Promise.reject(error)
        })
    } else {
      return uploadToTencentCOS({
        file: file,
        key: `group/photo/tencent-${filename}`
      })
        .then(() => {
          return `tencent-${filename}`
        })
        .catch((error) => {
          debugEvent({
            actionType: 'uploadToOSSError',
            data: JSON.stringify({ filename, error: error.message })
          })
          capturePromiseException(error)
          return Promise.reject(error)
        })
    }
  }

  const getOssUploadWay = (config) => {
    // oss_disable_qcloud == 1 时走默认, 或者 比例不存在
    if (
      config.oss_disable_qcloud == 1 ||
      !config.oss_service_traffic_ratio ||
      typeof config.oss_service_traffic_ratio !== 'string'
    ) {
      return config.default_oss_service || 'ali'
    } else {
      try {
        const ratioConfig = JSON.parse(config.oss_service_traffic_ratio)
        // 阿里云和腾讯配置的比例 大于100走默认
        if (ratioConfig.ali + ratioConfig.tencent > 100) {
          return config.default_oss_service || 'ali'
        }
        return (ratioConfig.ali + ratioConfig.tencent) * Math.random() <=
          ratioConfig.ali
          ? 'ali'
          : 'tencent'
      } catch (error) {
        return config.default_oss_service || 'ali'
      }
    }
  }

  const addPhotoToFolderAction = (photoID, folderID) => {
    return dispatch(
      uploadPhotosToFolders({
        groupID,
        folderIDs: [folderID],
        photoIDs: [String(photoID)],
        source: 7
      })
    )
      .then((data) => {
        return data.limitFolderIDs.length === 0
      })
      .catch((error) => {
        debugEvent({
          actionType: 'addPhotoToFolderError',
          data: JSON.stringify({ photoID, folderID, error: error.message })
        })
        return Promise.reject(error)
      })
  }

  const getUserCommentDecode = (userComment = '') => {
    if (userComment) {
      return dispatch(serverSideDecode({ data: userComment }))
    }
    return ''
  }

  const uploadPhotoAction = ({
    filename,
    fileSize,
    mediaType,
    exifInfo,
    userComment
  }: {
    filename: string
    fileSize: number
    mediaType: number
    exifInfo: ExifInfo
    userComment: UserComment
  }) => {
    let imageInfo: { fileSize: number; width?: number; height?: number } = { fileSize }
    if (mediaType === 0) {
      imageInfo.width = exifInfo?.width
      imageInfo.height = exifInfo?.height
    }

    let params: UploadPhotoParams = {
      user_id: userID,
      group_ids: [groupID],
      file_name: filename,
      time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      mediaType: mediaType,
      imageInfo
    }

    //今拍
    if (userComment.data) {
      const newUserComment = produce(userComment, (draft) => {
        draft.data!.fileName = filename
      })
      params.userComment = newUserComment
      params.location = newUserComment?.data?.baseInfo?.location
      params.lat = Number(newUserComment?.data?.baseInfo?.latitude)
      params.lng = Number(newUserComment?.data?.baseInfo?.longitude)
      params.time = newUserComment?.data?.baseInfo?.time || params.time
      params.sourceType = 0
    }
    //非今拍or视频，无法读取exif信息, 但必须提交以下userComment内容
    if (userComment.data2) {
      const newUserComment = produce(userComment, (draft) => {
        draft.data2!.fileName = filename
        draft.data2!.userID = userID
      })
      params.userComment = newUserComment
      params.sourceType = 1
    }
    return dispatch(uploadPhoto(params)).then((data) => {
      if (data.status === 0 && data.photoIDMap) {
        return data.photoIDMap[Number(inviteID)]
      }
      debugEvent({
        actionType: 'uploadPhotoError',
        data: JSON.stringify({ data })
      })
      return Promise.reject(data)
    })
  }

  const getFolderID = (groupID) => {
    return dispatch(
      createFolder({
        groupID,
        folderName: '上传的照片/视频',
        source: 7,
        parentFolderID: 0
      })
    )
      .then((data) => {
        if (data.status === 0) {
          return {
            folderID: data.folderID,
            photoCount: data.photoCount
          }
          // } else if (data.status === ExceptionStatus.noFolderUploadPermission) {
          //   Modal.warning({
          //     title: '您没有权限上传文件夹，请联系客服处理',
          //     okText: '我知道了'
          //   })
        } else {
          return Promise.reject(data)
        }
      })
      .catch((error) => {
        if (error.status === ExceptionStatus.noFolderUploadPermission) {
          Modal.warning({
            title: '暂无权限，请联系管理员处理',
            content: '「团队照片查看权限」为「所有人」才可支持上传',
            okText: '我知道了'
          })
        } else {
          Modal.warning({
            title: '系统错误，请联系客服处理',
            okText: '我知道了'
          })
        }
        return Promise.reject(error)
      })
  }

  const handleOverFolderLimit = () => {
    stopSignal.current = true
    debugEvent({ actionType: 'overFolderLimit' })
    Modal.warning({
      title: '超过单个文件夹数量限制，上传已暂停 !',
      content: `单个文件夹最多存储 ${folderMaxFileNum}张照片，请整理文件夹后上传剩下的照片/视频。`,
      okText: '我知道了',
      onOk: () => {
        setPercent(0)
        setFailed(true)
      }
    })
  }

  const beforeClose = () => {
    setPercent(0)
    setFailed(false)
    setCurrentIndex(0)
    setFolderID(0)
  }

  const handleCloseClick = () => {
    const { photosNum, videosNum } = restFiles
    if (done) {
      beforeClose()
      onClose()
      return
    }
    Modal.confirm({
      title: '上传任务未完成',
      content: `剩余${photosNum}张照片、${videosNum}个视频未上传成功，是否要离开页面？`,
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        stopSignal.current = true
        sensors.track('web_get_action_bigbatch_upload_tips', {
          actionType: 'unComplete',
          content: `剩余${photosNum}张照片、${videosNum}个视频未上传成功，是否要离开页面？`,
          groupID,
          role,
          clickItem: 'confirm'
        })
        beforeClose()
        onClose()
      },
      onCancel: () => {
        sensors.track('web_get_action_bigbatch_upload_tips', {
          actionType: 'unComplete',
          content: `剩余${photosNum}张照片、${videosNum}个视频未上传成功，是否要离开页面？`,
          groupID,
          role,
          clickItem: 'cancel'
        })
      }
    })
  }

  if (visible) {
    if (done) {
      return (
        <div>
          <StatusDialog
            fileList={fileList}
            onClose={handleCloseClick}
            folderID={folderID}
            currentIndex={currentIndex}
          />
        </div>
      )
    }
    return (
      <div>
        <ProgressDialog
          fileList={fileList}
          percent={percent}
          onClose={handleCloseClick}
        />
      </div>
    )
  }
  return null
}

export default forwardRef(UploadDialog)
